import {ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, inject, Input, Output, ViewChild} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {OverlayModule} from '@angular/cdk/overlay';
import {A11yModule} from '@angular/cdk/a11y';
import {MaterialModule} from '../../../material.module';

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

/** Also reads existing single-value filters when editing saved criteria. */
export function selectionValues(value: unknown): string[] {
  const values = Array.isArray(value) ? value : typeof value === 'string' && value ? [value] : [];
  return [...new Set(values.filter((item): item is string => typeof item === 'string' && item.length > 0))];
}

@Component({
  selector: 'app-filter-multiselect',
  standalone: true,
  imports: [MaterialModule, OverlayModule, A11yModule],
  templateUrl: './filter-multiselect.component.html',
  styleUrls: ['./filter-multiselect.component.scss'],
  providers: [{provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => FilterMultiselectComponent), multi: true}],
})
export class FilterMultiselectComponent implements ControlValueAccessor {
  private readonly changeDetector = inject(ChangeDetectorRef);
  private static nextId = 0;
  readonly panelId = `enrich-filter-options-${FilterMultiselectComponent.nextId++}`;
  @Input() label = '';
  @Input() options: FilterOption[] = [];
  @Input() loading = false;
  @Input() loadError = false;
  @Output() selectionChange = new EventEmitter<void>();
  @ViewChild('trigger') trigger?: ElementRef<HTMLInputElement>;
  @ViewChild('optionSearch') optionSearch?: ElementRef<HTMLInputElement>;
  selected: string[] = [];
  query = '';
  opened = false;
  disabled = false;
  panelWidth = 300;
  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  get summary(): string {
    if (!this.selected.length) return '';
    if (this.selected.length > 1) return `${this.selected.length} selected`;
    return this.options.find(option => option.value === this.selected[0])?.label || this.selected[0];
  }

  get filteredOptions(): FilterOption[] {
    // A saved value may not exist in a limited development dataset. Keep it visible and editable.
    const known = new Set(this.options.map(option => option.value));
    const available = [...(this.loadError ? [] : this.options), ...this.selected.filter(value => this.loadError || !known.has(value)).map(value => ({value, label: value, count: 0}))];
    const query = this.query.trim().toLocaleLowerCase();
    return available.filter(option => `${option.value} ${option.label}`.toLocaleLowerCase().includes(query));
  }

  get selectedOptions(): FilterOption[] {
    const optionsByValue = new Map(this.options.map(option => [option.value, option]));
    return this.selected.map(value => optionsByValue.get(value) || {value, label: value});
  }

  writeValue(value: unknown): void {
    this.selected = selectionValues(value);
    this.changeDetector.markForCheck();
  }

  registerOnChange(fn: (value: string[]) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    if (disabled) this.close();
    this.changeDetector.markForCheck();
  }

  open(): void {
    if (this.disabled || this.opened) return;
    this.query = '';
    this.panelWidth = Math.min(Math.max(this.trigger?.nativeElement.closest('mat-form-field')?.getBoundingClientRect().width || 300, 280), window.innerWidth - 24);
    this.opened = true;
  }

  close(): void {
    if (!this.opened) return;
    this.opened = false;
    this.onTouched();
  }

  onTriggerKey(event: KeyboardEvent): void {
    if (['ArrowDown', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      this.open();
    }
  }

  onPanelKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close();
    }
  }

  toggle(value: string, checked: boolean): void {
    if (this.disabled || (checked && (this.loading || this.loadError || this.selected.length >= 100))) return;
    this.selected = checked ? [...new Set([...this.selected, value])] : this.selected.filter(item => item !== value);
    this.onChange([...this.selected]);
    this.selectionChange.emit();
  }

  remove(value: string): void {
    this.toggle(value, false);
    // Keep keyboard focus inside the panel after the removal button disappears.
    this.optionSearch?.nativeElement.focus();
  }

  clear(): void {
    if (this.disabled) return;
    this.selected = [];
    this.onChange([]);
    this.selectionChange.emit();
  }
}
